'use strict';

import _ from 'underscore';
import $ from 'jquery';
import {getLogger} from 'loglevel';
import mediator from '../../js/mediator';
import config from '../../js/config';
import utilities from '../../js/utilities';
import {parallelArrayEach} from '../../utilities/uiLoopHelpers';
import ProductPricingService from '../../services/ProductPricingService';
import Analytics from '../Analytics/Analytics';
import ProductImpressionList from './ProductImpressionList';
import ProductImpression from './ProductImpression';

const log = getLogger('ProductImpressions');

const IMPRESSION_TRACK_THROTTLE_MILLISECONDS = 50;

class ProductImpressions {
  initialize() {
    this.productLists = {};
    this.productImpressionsStack = [];
    this.productPricingStack = [];
    this.throttledTrackImpressions = _.throttle(this.trackImpressions.bind(this), IMPRESSION_TRACK_THROTTLE_MILLISECONDS, { leading: false });

    this.bindEvents();
  }

  bindEvents() {
    mediator.on(config.events.productImpressions.viewed, this.productImpressionViewed, this);
    mediator.on(config.events.productImpressions.check, this.checkListsInViewport, this);

    mediator.on(config.events.productImpressions.productElementsHidden, ProductImpression.setProductElementsAsHidden);
    mediator.on(config.events.productImpressions.productElementsVisible, ProductImpression.setProductElementsAsVisible);

    mediator.on(config.events.ui.scrollThrottle, this.checkAllListsInViewport, this);
    mediator.on(config.events.ui.resizeThrottle, this.checkAllListsInViewport, this);
    mediator.on(config.events.pricing.received, this.onPricingReceived, this);
  }

  /**
   * Add this element to the list of product impression lists to watch.
   *
   * @param {HTMLElement} el
   */
  addList(el, startIndex = 0) {
    let $list = $(el);

    // Get all of the products in the list
    let $products = ProductImpressionList.getProductsInListElement($list);

    // There are no products in this list so ignore it
    if (!$products.length) {
      return;
    }

    // Create the list
    let list = new ProductImpressionList($list, $products, startIndex);
    this.productLists[list.name] = list;
    log.debug(`ProductImpression: List added '${list.name}'`);
  }

  /**
   * Check all lists to see if any of them are in the viewport.
   */
  checkAllListsInViewport() {
    this.checkListsInViewport(this.productLists);
  }

  /**
   * Check if any of the given lists are in the viewport.
   *
   * @param {Object.<string, ProductImpressionList>} productLists
   */
  checkListsInViewport(productLists) {
    // Only need to continue if we have something to check
    if (!_.size(productLists)) {
      return;
    }

    let viewport = utilities.getViewportDimensions();

    parallelArrayEach(productLists, productList => productList.impressionCheck(viewport));
  }

  /**
   * There are some impressions on the stack - so add them to the data layer.
   */
  trackImpressions() {
    // Clear the stack and get the products so far
    let products = this.productImpressionsStack.splice(0, this.productImpressionsStack.length);

    _.chain(products)
      .groupBy('list')
      .forEach(list => {
        // Generate the analytics impression information
        let impressions = _.map(list, product => product.getDatalayerImpression());
        Analytics.ecommercePushWithCurrency(config.analytics.eventTags.productListImpressionEcommerce, { impressions });
      });
  }

  /**
   * This product has been viewed for the required amount of time, so create an impression for it.
   *
   * The impression can only be made fully when the pricing information is available.
   *
   * @param {ProductImpression} product
   */
  productImpressionViewed(product) {
    // Check to see if we have pricing information available or not
    if (!product.waitingForPricing()) {
      this.addImpression(product);
    } else {
      this.addImpressionWhenPricingReady(product);
    }

    // Remove the product as we don't want multiple impressions
    this.removeProductFromList(product);
  }

  /**
   * Removes a product from the list so that it will not create multiple impressions, by flagging it as viewed.
   *
   * Additionally removes the list once it is empty.
   *
   * @param {ProductImpression} product
   */
  removeProductFromList(product) {
    let productList = this.productLists[product.list];

    // Not found for some reason
    if (!productList) {
      log.warn(`ProductImpression: List '${product.list}' not found`);
      return;
    }

    // Flag the product as viewed
    product.viewed = true;

    // Check if the list has now been completely viewed
    if (_.every(productList.products, { viewed: true })) {
      log.debug(`ProductImpression: Removing list '${product.list}'`);
      delete this.productLists[product.list];
    }
  }

  removeList(el) {
    let $list = $(el);
    let listName = $list.data('gtmProductImpression');

    // See if the list exists
    if (!this.productLists[listName]) {
      return;
    }

    // Stop any active timers
    this.productLists[listName].clearAllTimers();

    // Remove it
    delete this.productLists[listName];
  }

  /**
   * Pricing information has been received so check if any impressions are needing the pricing data.
   *
   * @param {object[]} parts
   */
  onPricingReceived(parts) {
    // Only need to continue if we have something to check
    if (!this.productPricingStack.length) {
      return;
    }

    // Check each waiting product impression to see if its pricing has loaded
    for (let i = this.productPricingStack.length - 1; i >= 0; i--) {
      let product = this.productPricingStack[i];
      let part = ProductPricingService.findBuyingOption(parts, product.id, product.name, product.brand);

      // The part wasn't found - or it had no price set
      if (!part || _.isUndefined(product.el.dataset.price)) {
        continue;
      }

      this.addImpression(product);

      // Remove this product impression as it has been dealt with now
      this.productPricingStack.splice(i, 1);
    }
  }

  /**
   * Push the impression on to the stack so that we can group product impressions within a short space of time.
   *
   * @param {ProductImpression} product
   */
  addImpression(product) {
    log.debug(`ProductImpression: Impression ${product}`);
    this.productImpressionsStack.push(product);
    this.throttledTrackImpressions();
  }

  /**
   * Waiting for pricing to load before pushing the impression.
   *
   * @param {ProductImpression} product
   */
  addImpressionWhenPricingReady(product) {
    log.debug(`ProductImpression: Waiting for pricing ${product}`);
    this.productPricingStack.push(product);
  }
}

let productImpressions = new ProductImpressions();

export default productImpressions;

export function createProductImpressions($parent) {
  parallelArrayEach($parent.find('.gtm-productImpression'), el => productImpressions.addList(el));
}



// WEBPACK FOOTER //
// ./assets/components/ProductImpressions/ProductImpressions.js